import { PageTabs } from "@/components/ui/page-tabs";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";
import { ProcedureInfo } from "./components/ProcedureInfo";
import { Outlet, useLocation, useParams } from "react-router";
import { useGetFormalityRequestDetail } from "@/queries/formaly-request";
import { cn } from "@/utils/theme";
import { InfoProvider, useInfo } from "./hooks/use-info";
import { History } from "./components/history";
import { DeadlineTimer } from "./components/deadline-timer";
import { SuspensionModal } from "./components/SuspensionModal";
import { useGetSuspensionRequests } from "@/queries/deadline-suspensions/deadline-suspensions.api";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { Renderify } from "renderify-js";
import SignRequestHistory from "./components/sign-request-history";
import { CreateCancelRequestModal } from "./components/cancelation/cancel-request-modal";

type ProceduresListTabs = "detalles" | "historial";

const tableDetailTabs: { label: string; key: ProceduresListTabs }[] = [
  { label: "Detalles", key: "detalles" },
  { label: "Historial", key: "historial" },
];

const Header = ({
  title,
  deadline,
  activeTab,
  canCancel,
  onTabChange,
  onDeadlineExpire,
  onSuspensionRequest,
  onCancelRequest,
  shouldShowInfo,
  canCreateSuspension,
  isCancelled,
}: {
  title?: string;
  deadline?: Date;
  activeTab: ProceduresListTabs;
  onTabChange: (key: ProceduresListTabs) => void;
  onDeadlineExpire: () => void;
  onSuspensionRequest?: () => void;
  onCancelRequest: () => void;
  shouldShowInfo: boolean;
  canCreateSuspension: boolean;
  canCancel: boolean;
  isCancelled: boolean;
}) => {
  return (
    <div className="flex pt-2 px-4 gap-8">
      <div
        className={cn(
          "border-neutral border-b border-solid flex w-full items-center",
          !shouldShowInfo && "pb-1",
        )}
      >
        <Heading variant="h6" className="flex-auto truncate">
          {title ?? ""}
        </Heading>
        {!shouldShowInfo && (
          <DeadlineTimer deadline={deadline} onExpire={onDeadlineExpire} />
        )}

        {shouldShowInfo && (
          <PageTabs
            tabs={tableDetailTabs}
            activeKey={activeTab}
            showBorder={false}
            className="px-3"
            onTabChange={(key) => onTabChange(key as ProceduresListTabs)}
          />
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="quiet" size="icon" aria-label="Settings">
              <Icon type="far" name="ellipsis-vertical" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[222px] min-w-56 rounded-lg border-neutral-silent text-body font-normal"
            side="left"
            align="start"
          >
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                onClick={onSuspensionRequest}
                disabled={!canCreateSuspension || isCancelled}
              >
                Solicitar suspensión
              </DropdownMenuItem>
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3 text-danger cursor-pointer"
                onClick={onCancelRequest}
                disabled={!canCancel || isCancelled}
              >
                Cancelar trámite
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const Content = () => {
  const [activeTab, setActiveTab] = useState<ProceduresListTabs>("detalles");
  const { procedureId, singRequestId } = useParams<{
    procedureId: string;
    singRequestId: string;
  }>();
  const {
    shouldShowInfo,
    formDeadline,
    canBeCancelled,
    isCancelled,
    setIsCancelled,
  } = useInfo();
  const [modalState, setModalState] = useState<
    "idle" | "suspension" | "cancel-request"
  >("idle");
  const { hasPermission } = useAuthorization();

  const { data, isPending } = useGetFormalityRequestDetail(procedureId ?? "");

  const { data: suspensionRequest, refetch: refetchSuspensionRequests } =
    useGetSuspensionRequests(procedureId ?? "", isCancelled ? 0 : 10000);

  const mainByTab: Record<ProceduresListTabs, React.ReactNode> = useMemo(
    () => ({
      detalles: <Outlet />,
      historial: singRequestId ? (
        <SignRequestHistory />
      ) : (
        <History formalityId={procedureId ?? ""} />
      ),
    }),
    [procedureId, singRequestId],
  );
  const canCreateSuspensionReq = hasPermission(
    Permissions["node-deadline-suspensions"].create,
  );
  const canCreateSuspension = useMemo(() => {
    if (!canCreateSuspensionReq) return false;
    if (!suspensionRequest) return false;
    return suspensionRequest?.data?.can_create_new ?? false;
  }, [suspensionRequest, canCreateSuspensionReq]);

  useEffect(() => {
    if (!data) return;
    if (!setIsCancelled) return;
    if (data?.status?.toUpperCase() === "CANCELED") {
      setIsCancelled(true);
      return;
    }
    if (
      !data?.cancellation_requests ||
      data.cancellation_requests.length === 0
    ) {
      setIsCancelled(false);
      return;
    }

    const isCancelled = data.cancellation_requests.some(
      (request) =>
        (request.approved_at == null && request.denied_at == null) || // Pending
        (request.approved_at != null && request.denied_at == null), // Approved
    );
    setIsCancelled(isCancelled);
  }, [data?.cancellation_requests, data?.status, setIsCancelled]);

  const handleExpiration = () => {
    /** Comentado por el momento */
    // setFormDeadline(undefined);
    // showToast({
    //   title: "Formulario expirado",
    //   description: "El tiempo para enviar las correcciones ha expirado",
    //   options: {
    //     type: "error",
    //   },
    // });
    // navigate(`/tramite/${procedureId}`, { replace: true });
  };

  const handleSubmitSuspension = (shouldRefetch: boolean) => {
    if (shouldRefetch) {
      refetchSuspensionRequests();
    }
    setModalState("idle");
  };

  const location = useLocation();

  const isInternalForm = location.pathname.includes("formulario-interno");
  const hasGroups = !!data?.groups && data.groups.length > 0;

  return (
    <div
      className={cn(isInternalForm && "h-full flex flex-col overflow-hidden")}
    >
      <Renderify condition={!isInternalForm}>
        <Header
          title={data?.process?.name || ""}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          shouldShowInfo={shouldShowInfo}
          deadline={formDeadline}
          onDeadlineExpire={handleExpiration}
          canCreateSuspension={canCreateSuspension}
          onSuspensionRequest={() => setModalState("suspension")}
          onCancelRequest={() => setModalState("cancel-request")}
          isCancelled={!!isCancelled}
          canCancel={canBeCancelled && hasGroups} // TODO: add user permission check
        />
      </Renderify>

      <div
        className={cn(
          "flex flex-col-reverse laptop:flex-row px-4 pt-8 pb-4 gap-8 max-[1280px]:flex-wrap-reverse overflow-hidden",
          isInternalForm && "flex-1",
        )}
      >
        <div className={"flex-1 w-full overflow-hidden"}>
          {mainByTab[activeTab]}
        </div>

        <Renderify condition={shouldShowInfo && !isInternalForm}>
          <ProcedureInfo
            data={data!}
            isLoading={isPending}
            isCancelled={isCancelled}
          />
        </Renderify>
        <SuspensionModal
          onClose={() => setModalState("idle")}
          onSubmit={handleSubmitSuspension}
          open={modalState === "suspension"}
          tramiteId={procedureId ?? ""}
          suspensionRequest={suspensionRequest}
        />
        <CreateCancelRequestModal
          isOpen={modalState === "cancel-request"}
          onClose={() => setModalState("idle")}
          requestCode={data?.code || ""}
          requestId={procedureId ?? ""}
        />
      </div>
    </div>
  );
};

const ProcedureDetailPage = () => {
  return (
    <InfoProvider>
      <Content />
    </InfoProvider>
  );
};

export { ProcedureDetailPage };
